import { Head, router, useForm } from "@inertiajs/react"
import {
  GraduationCap,
  Plus,
  Pencil,
  Trash2,
  ArrowUp,
  ArrowDown,
} from "lucide-react"
import { useMemo, useState } from "react"
import { AdminLayout } from "../components/AdminLayout"
import { SectionTabs } from "../components/SectionTabs"

const settingsTabs = [
  { label: "General", href: "/app/admin/settings" },
  { label: "Tutorials", href: "/app/admin/tutorial_videos" },
]
import { buildVideoEmbedUrl } from "@/lib/video-embed"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
} from "@/components/ui/responsive-dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

// Audience: which portal a tutorial appears on. "everyone" is the "Both" value
// (one row, both pages). Single-select — a video has exactly one audience.
const AUDIENCE_OPTIONS = [
  { value: "coach", label: "Coaches" },
  { value: "member", label: "Members" },
  { value: "everyone", label: "Both" },
]
const AUDIENCE_LABELS = { coach: "Coaches", member: "Members", everyone: "Both" }
const AUDIENCE_BADGE_CLASS = {
  coach: "bg-blue-100 text-blue-700 hover:bg-blue-100",
  member: "bg-purple-100 text-purple-700 hover:bg-purple-100",
  everyone: "bg-amber-100 text-amber-700 hover:bg-amber-100",
}

function AudienceBadge({ audience }) {
  return (
    <Badge className={AUDIENCE_BADGE_CLASS[audience] || AUDIENCE_BADGE_CLASS.coach}>
      {AUDIENCE_LABELS[audience] || AUDIENCE_LABELS.coach}
    </Badge>
  )
}

function VideoDialog({ open, onOpenChange, video = null }) {
  const isEditing = !!video
  const form = useForm({
    title: video?.title || "",
    vimeo_url: video?.vimeoUrl || "",
    description: video?.description || "",
    position: video?.position ?? 0,
    status: video?.status || "draft",
    audience: video?.audience || "coach",
  })

  // Live preview (U8): resolve whatever the admin pastes through the same
  // helper the players use, so they confirm the right video before saving and
  // get told immediately when a link isn't recognized.
  const trimmedUrl = form.data.vimeo_url?.trim()
  const previewUrl = trimmedUrl ? buildVideoEmbedUrl(form.data.vimeo_url) : null

  const handleSubmit = (e) => {
    e.preventDefault()

    if (isEditing) {
      form.patch(`/app/admin/tutorial_videos/${video.id}`, {
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
      })
    } else {
      form.post("/app/admin/tutorial_videos", {
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
      })
    }
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              {isEditing ? "Edit Tutorial Video" : "Add Tutorial Video"}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {isEditing
                ? "Update this tutorial video's details."
                : "Add a new tutorial video. Paste the Vimeo address-bar URL, Share link, or embed code, then choose who sees it."}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="Dashboard Overview"
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.title}
                onChange={(e) => form.setData("title", e.target.value)}
              />
              {form.errors.title && (
                <p className="text-sm text-red-600">{form.errors.title}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="vimeo_url">Vimeo link</Label>
              <Input
                id="vimeo_url"
                placeholder="Vimeo link, /manage URL, embed code, or video ID"
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.vimeo_url}
                onChange={(e) => form.setData("vimeo_url", e.target.value)}
              />
              <p className="text-xs text-zinc-500">
                Paste whatever Vimeo gives you — the address-bar URL, the Share
                link (with or without a privacy hash), the player URL, the embed
                code, or a bare ID.
              </p>
              {form.errors.vimeo_url && (
                <p className="text-sm text-red-600">{form.errors.vimeo_url}</p>
              )}
              {trimmedUrl &&
                (previewUrl ? (
                  <div className="mt-1 overflow-hidden rounded-md border border-zinc-200">
                    <div
                      className="relative w-full"
                      style={{ paddingTop: "56.25%" }}
                    >
                      <iframe
                        src={previewUrl}
                        className="absolute inset-0 h-full w-full"
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        title="Vimeo preview"
                      />
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-amber-600">
                    We couldn't recognize that as a Vimeo or YouTube link. Paste
                    the address-bar URL, the Share link, or the embed code.
                  </p>
                ))}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Step-by-step walkthrough of the feature..."
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 min-h-[120px]"
                value={form.data.description}
                onChange={(e) => form.setData("description", e.target.value)}
              />
              {form.errors.description && (
                <p className="text-sm text-red-600">
                  {form.errors.description}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="audience">Audience</Label>
              <Select
                value={form.data.audience}
                onValueChange={(value) => form.setData("audience", value)}
              >
                <SelectTrigger
                  id="audience"
                  className="bg-white border-zinc-300 text-zinc-900"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUDIENCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500">
                "Both" shows the video on the coach and member tutorial pages
                from this one entry.
              </p>
              {form.errors.audience && (
                <p className="text-sm text-red-600">{form.errors.audience}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="position">Position</Label>
                <Input
                  id="position"
                  type="number"
                  min="0"
                  className="bg-white border-zinc-300 text-zinc-900"
                  value={form.data.position}
                  onChange={(e) =>
                    form.setData("position", parseInt(e.target.value) || 0)
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex items-center gap-3 pt-2">
                  <Switch
                    checked={form.data.status === "published"}
                    onCheckedChange={(checked) =>
                      form.setData("status", checked ? "published" : "draft")
                    }
                  />
                  <span className="text-sm text-zinc-700">
                    {form.data.status === "published" ? "Published" : "Draft"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <ResponsiveDialogFooter className="px-4 sm:px-0">
            <Button
              type="submit"
              variant="gradient"
              disabled={form.processing}
            >
              {form.processing
                ? "Saving..."
                : isEditing
                  ? "Save Changes"
                  : "Create Video"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

export default function TutorialVideosIndex({
  currentUser,
  videos,
  pendingCount,
}) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingVideo, setEditingVideo] = useState(null)
  const [dialogKey, setDialogKey] = useState(0)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [audienceFilter, setAudienceFilter] = useState("all")

  // Client-side filter over the already-loaded list (A5). Matches on the row's
  // literal stored audience, so a "Both" row shows under the Both chip only.
  const filteredVideos = useMemo(
    () =>
      audienceFilter === "all"
        ? videos
        : videos.filter((v) => v.audience === audienceFilter),
    [videos, audienceFilter]
  )

  const handleEdit = (video) => {
    setEditingVideo(video)
    setDialogKey(k => k + 1)
    setDialogOpen(true)
  }

  const handleCreate = () => {
    setEditingVideo(null)
    setDialogKey(k => k + 1)
    setDialogOpen(true)
  }

  const handleDelete = () => {
    if (deleteTarget) {
      router.delete(`/app/admin/tutorial_videos/${deleteTarget.id}`, {
        preserveScroll: true,
        onSuccess: () => setDeleteTarget(null),
      })
    }
  }

  return (
    <AdminLayout
      currentUser={currentUser}
      pendingCount={pendingCount}
    >
      <Head title="Tutorial Videos - Admin - Kingdom Factor" />

      <div className="space-y-6">
        <SectionTabs tabs={settingsTabs} />

        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-zinc-900">
              Tutorial Videos
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Manage coach and member tutorial videos
            </p>
          </div>
          <Button variant="gradient" onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Add Video
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Label htmlFor="audience-filter" className="text-sm text-zinc-500">
            Audience
          </Label>
          <Select value={audienceFilter} onValueChange={setAudienceFilter}>
            <SelectTrigger
              id="audience-filter"
              className="w-40 bg-white border-zinc-300"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="coach">Coaches</SelectItem>
              <SelectItem value="member">Members</SelectItem>
              <SelectItem value="everyone">Both</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12 sm:w-16">#</TableHead>
                <TableHead>Title</TableHead>
                <TableHead className="hidden lg:table-cell">Vimeo URL</TableHead>
                <TableHead className="hidden sm:table-cell">Audience</TableHead>
                <TableHead className="hidden sm:table-cell">Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredVideos.map((video) => (
                <TableRow key={video.id}>
                  <TableCell className="font-mono text-sm text-zinc-500">
                    {video.position}
                  </TableCell>
                  <TableCell className="font-medium whitespace-normal">
                    <div className="min-w-0">
                      <p className="line-clamp-1">{video.title}</p>
                      <p className="text-xs text-zinc-500 truncate lg:hidden mt-0.5">
                        {video.vimeoUrl}
                      </p>
                      <div className="mt-1.5 flex items-center gap-2 sm:hidden">
                        <AudienceBadge audience={video.audience} />
                        <Badge
                          className={
                            video.status === "published"
                              ? "bg-green-500 text-white hover:bg-green-500"
                              : "bg-zinc-400 text-white hover:bg-zinc-400"
                          }
                        >
                          {video.status === "published" ? "Published" : "Draft"}
                        </Badge>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 max-w-[200px] truncate hidden lg:table-cell">
                    {video.vimeoUrl}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <AudienceBadge audience={video.audience} />
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <Badge
                      className={
                        video.status === "published"
                          ? "bg-green-500 text-white hover:bg-green-500"
                          : "bg-zinc-400 text-white hover:bg-zinc-400"
                      }
                    >
                      {video.status === "published" ? "Published" : "Draft"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(video)}
                        title="Edit video"
                        aria-label={`Edit ${video.title}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteTarget(video)}
                        title="Delete video"
                        aria-label={`Delete ${video.title}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredVideos.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-8 text-zinc-500"
                  >
                    {videos.length === 0
                      ? 'No tutorial videos yet. Click "Add Video" to create one.'
                      : "No tutorial videos match this audience filter."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      <VideoDialog
        key={dialogKey}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        video={editingVideo}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tutorial Video</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.title}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={handleDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  )
}
